import type { StepsProps } from "../../../utils/local-antd";
import isEmpty from "lodash/isEmpty";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Flex } from "../../../components/Flex";
import { FacebookIntegrationDto } from "../../../model/integration/dto/FacebookIntegrationDto";
import { IntegrationKind } from "../../../model/integration/types/IntegrationKind";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { InetgrationFacebookImportIntegrationStep } from "./facebookSteps/InetgrationFacebookImportIntegrationStep";
import { IntegrationFacebookFinalizeStep } from "./facebookSteps/IntegrationFacebookFinalizeStep";
import { IntegrationFaceookInitiateIntegrationStep } from "./facebookSteps/IntegrationFaceookInitiateIntegrationStep";
import { DrawerWithStepper } from "../../../components/DrawerWithStepper";
import { Modal } from "../../../components/modal/Modal";
import { Text } from "../../../components/Text";
import i18n from "../../../i18n";
import { Button } from "../../../components/Button";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";

const LangToForm: Record<AppLangs, string> = {
  [AppLangs.EN]: "68ca8411de12c59e86f8f9bf",
  [AppLangs.AR]: "68ca848cde12c59e86f8fa02",
  [AppLangs.TR]: "68ca846dde12c59e86f8f9de",
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export const IntegrationFacebookStepper: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const [resolvedFields, setResolvedFields] = useState<
    { userField: string; foreignField: string }[]
  >([]);
  const [formId, setFormId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const { t } = useTranslation(undefined, {
    keyPrefix: "integration.add-modal.facebook",
  });

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const facebookIntegration = useAppSelector(
    integrationSliceSelectors.selectFBIntegration,
  );

  const fbResolveFieldsData = useAppSelector(
    integrationSliceSelectors.selectFBResolveFieldsData,
  );

  const stepperItems: StepsProps["items"] = [
    // { title: t('modal.step.1.title') },
    { title: t("modal.step.2.title") },
    { title: t("modal.step.3.title") },
    { title: t("modal.step.4.title") },
  ];

  const onSubmit = async () => {
    try {
      if (isEmpty(facebookIntegration?.config.fieldsResolution)) {
        await onResolveFields();
      } else {
        await onUpdateResolvedFields();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const onResolveFields = async () => {
    try {
      if (!formId) {
        return;
      }

      setLoading(true);
      const mappedResolvedObject = resolvedFields.reduce(
        (acc, { userField, foreignField }) => {
          if (userField && foreignField) {
            (acc as any)[userField] = foreignField;
          }
          return acc;
        },
        {},
      );

      await dispatch(
        integrationActions.finishFBWebhookFinalizeFields({
          integrationId: facebookIntegration?._id!,
          resolvedFields: mappedResolvedObject,
          formId,
        }),
      ).unwrap();
      await dispatch(
        integrationActions.getUserIntegrations({ userId }),
      ).unwrap();
      AtiraToast.success(t("finalize.create.success"));
      onClose();
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onUpdateResolvedFields = async () => {
    try {
      setLoading(true);
      const mappedResolvedObject = resolvedFields.reduce(
        (acc, { userField, foreignField }) => {
          if (userField && foreignField) {
            (acc as any)[userField] = foreignField;
          }
          return acc;
        },
        {},
      );

      await dispatch(
        integrationActions.updateIntegration({
          userId,
          kind: IntegrationKind.FACEBOOK,
          integrationId: facebookIntegration?._id!,
          data: {
            fieldsResolution: mappedResolvedObject,
            formId,
          } as FacebookIntegrationDto,
        }),
      ).unwrap();
      await dispatch(
        integrationActions.getUserIntegrations({ userId }),
      ).unwrap();
      AtiraToast.success(t("finalize.update.success"));
      onClose();
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isEmpty(facebookIntegration?.config.fieldsResolution)) {
      setResolvedFields(
        Object.entries(facebookIntegration?.config.fieldsResolution).map(
          ([userField, foreignField]) => ({
            userField,
            foreignField,
          }),
        ),
      );

      setFormId(facebookIntegration?.config?.formId || "");
    } else if (!isEmpty(fbResolveFieldsData?.foreignFields)) {
      setResolvedFields(
        Array.from(
          {
            length: Object.keys(fbResolveFieldsData?.foreignFields || {})
              .length,
          },
          (_, i) => ({
            userField: i === 0 ? "email" : "",
            foreignField: "",
          }),
        ),
      );
    }

    setFormId(facebookIntegration?.config?.formId || "");
  }, [
    facebookIntegration?.config.fieldsResolution,
    facebookIntegration?.config?.formId,
    fbResolveFieldsData?.foreignFields,
  ]);

  return (
    <Fragment>
      <DrawerWithStepper
        open={open}
        onClose={onClose}
        title={t("modal.title")}
        stepperItems={stepperItems}
        onSubmit={onSubmit}
        submitLoading={loading}
        steps={[
          <IntegrationFaceookInitiateIntegrationStep />,
          <InetgrationFacebookImportIntegrationStep />,
          <IntegrationFacebookFinalizeStep
            fields={resolvedFields}
            setFields={setResolvedFields}
            setFormId={setFormId}
            formId={formId}
          />,
        ]}
        stepsExtraComponent={
          <Button
            width="6rem"
            padding={"s"}
            hover
            onClick={() => setHelpModalOpen(true)}
          >
            {t("helpModal.button")}
          </Button>
        }
      />

      <Modal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        title={t("helpModal.title")}
      >
        <Text>{t("helpModal.description")}</Text>
        <Flex>
          <iframe
            title="Facebook Leads Integration Help"
            data-form-title="facebook integration"
            height="500"
            width="500"
            style={{ border: "none" }}
            src={`https://atiraspace.com/api/form/${LangToForm[i18n.language as AppLangs]}/generate`}
          ></iframe>
        </Flex>
      </Modal>
    </Fragment>
  );
};
